import { useState, useEffect, useCallback } from "react";
import { GfFlyerItemDto } from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../utils/api";

/**
 * Props for the useAllFlyerItems hook.
 *
 * @property flyerItemListId - ID of the flyer item list to fetch items from
 * @property authHeaders - Authentication headers for API requests
 * @property enabled - Whether to fetch items (useful for lazy loading when modals open). Default: true
 */
interface UseAllFlyerItemsProps {
  flyerItemListId?: string;
  authHeaders: { headers: { Authorization: string } };
  enabled?: boolean;
}

/**
 * Return type for the useAllFlyerItems hook.
 *
 * @property allFlyerItems - Complete array of all flyer items (not paginated)
 * @property isLoading - Whether items are currently being fetched
 * @property error - Error object if fetch failed, null otherwise
 * @property refetch - Function to manually refetch all items, bypassing cache
 * @property addItem - Function to add an item to local state (e.g., after creating new item)
 * @property removeItem - Function to remove an item from local state by ID (e.g., after deletion)
 * @property updateItem - Function to update an existing item in local state by matching on item.id
 */
interface UseAllFlyerItemsReturn {
  allFlyerItems: GfFlyerItemDto[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  reset: () => void;
  addItem: (item: GfFlyerItemDto) => void;
  removeItem: (itemId: string) => void;
  updateItem: (item: GfFlyerItemDto) => void;
}

/**
 * Custom hook to fetch all flyer items (not paginated) for a given flyer item list.
 *
 * Designed for modal selection dialogs and other components that need the complete item list
 * without pagination. Includes intelligent caching to prevent unnecessary refetches and helper
 * functions for local state management.
 *
 * Key features:
 * - Fetches all items with high limit (1000) to bypass pagination
 * - Caches results to prevent redundant API calls
 * - Only fetches when enabled (useful for lazy loading)
 * - On failure sets `error` and keeps the last full list (never a partial page of rows)
 * - Provides addItem/removeItem helpers for optimistic updates
 * - Manual refetch function to bypass cache
 * - Orders items by name in ascending order
 *
 * @param props - Hook configuration options
 * @returns Object with allFlyerItems array, loading state, error, and helper functions
 */
export const useAllFlyerItems = ({
  flyerItemListId,
  authHeaders,
  enabled = true,
}: UseAllFlyerItemsProps): UseAllFlyerItemsReturn => {
  const [allFlyerItems, setAllFlyerItems] = useState<GfFlyerItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchAllItems = useCallback(async () => {
    if (!flyerItemListId || !enabled) {
      return;
    }

    if (hasFetched) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response =
        await adminApiService.gfFlyerItemAdminControllerGetManyFlyerItem(
          {
            page: 1,
            limit: 1000,
            flyerItemListId,
            orderBy: JSON.stringify({
              orderBy: {
                name: "ASC",
              },
            }),
          },
          authHeaders,
        );

      if (!response) {
        setError(new Error("Failed to fetch items"));
        return;
      }
      setHasFetched(true);
      setAllFlyerItems(response.data ?? []);
    } catch (err) {
      console.error("Error fetching all flyer items:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch items"));
    } finally {
      setIsLoading(false);
    }
  }, [flyerItemListId, authHeaders, enabled, hasFetched]);

  useEffect(() => {
    if (enabled && flyerItemListId) {
      fetchAllItems();
    }
  }, [enabled, flyerItemListId, fetchAllItems]);

  /**
   * Manual refetch function that bypasses the hasFetched cache.
   * Forces a fresh fetch from the API regardless of current state.
   */
  const refetch = useCallback(async () => {
    setHasFetched(false);
    await fetchAllItems();
  }, [fetchAllItems]);

  /**
   * Resets the fetch cache so the next render cycle re-fetches from the API.
   * Useful when external data changes (e.g. after a retry job completes).
   */
  const reset = useCallback(() => {
    setHasFetched(false);
  }, []);

  /**
   * Adds a new item to the local state.
   * Useful for optimistic updates after creating a new item.
   *
   * @param item - The flyer item to add
   */
  const addItem = useCallback((item: GfFlyerItemDto) => {
    setAllFlyerItems((prev) => [...prev, item]);
  }, []);

  /**
   * Removes an item from local state by ID.
   * Useful for optimistic updates after deleting an item.
   *
   * @param itemId - ID of the item to remove
   */
  const removeItem = useCallback((itemId: string) => {
    setAllFlyerItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  /**
   * Updates an existing item in local state by matching on item.id.
   * Useful for in-place updates after editing an item's details without
   * triggering a full refetch (which would unmount dependent components).
   *
   * @param item - The updated flyer item (must have a valid id)
   */
  const updateItem = useCallback((item: GfFlyerItemDto) => {
    setAllFlyerItems((prev) =>
      prev.map((existing) => (existing.id === item.id ? item : existing)),
    );
  }, []);

  return {
    allFlyerItems,
    isLoading,
    error,
    refetch,
    reset,
    addItem,
    removeItem,
    updateItem,
  };
};
